import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import Login from "@/pages/Login";
import BAA from "@/pages/BAA";
import Reset2FA from "@/pages/Reset2FA";
import Dashboard from "@/pages/Dashboard";
import Charting from "@/pages/Charting";
import PriorAuth from "@/pages/PriorAuth";
import RevenueCycle from "@/pages/RevenueCycle";
import Liability from "@/pages/Liability";
import Referrals from "@/pages/Referrals";
import Staffing from "@/pages/Staffing";
import EHRBridge from "@/pages/EHRBridge";
import ManagedCare from "@/pages/ManagedCare";
import PatientEngagement from "@/pages/PatientEngagement";
import CareCoordination from "@/pages/CareCoordination";
import HospitalOps from "@/pages/HospitalOps";
import Analytics from "@/pages/Analytics";

// v2 modules
import PANPHub from "@/pages/PANPHub";
import VoiceEngine from "@/pages/VoiceEngine";
import Predictive from "@/pages/Predictive";
import LegalIntelligence from "@/pages/LegalIntelligence";
import PharmaManager from "@/pages/PharmaManager";
import CMETracker from "@/pages/CMETracker";
import PeerConsult from "@/pages/PeerConsult";
import FinancialWellness from "@/pages/FinancialWellness";
import RiskStratification from "@/pages/RiskStratification";
import EmergencyProtocols from "@/pages/EmergencyProtocols";
import Telehealth from "@/pages/Telehealth";
import DataIntelligence from "@/pages/DataIntelligence";
import ReputationManager from "@/pages/ReputationManager";
import ContractIntelligence from "@/pages/ContractIntelligence";
import Outcomes from "@/pages/Outcomes";
import Cardiology from "@/pages/Cardiology";
import HeartRisk from "@/pages/HeartRisk";

import Layout from "@/components/Layout";
import { auth } from "@/lib/api";

import TwoFASetup from "@/pages/TwoFASetup";
import Recovery from "@/pages/Recovery";
import SecurityAdmin from "@/pages/SecurityAdmin";
import AdminLeads from "@/pages/AdminLeads";
import LearningQueue from "@/pages/LearningQueue";
import PatientOutputsDemo from "@/pages/PatientOutputsDemo";
import Dictation from "@/pages/Dictation";
import EncounterPrep from "@/pages/EncounterPrep";
import TimeSaved from "@/pages/TimeSaved";
import EODMode from "@/pages/EODMode";
import Wellbeing from "@/pages/Wellbeing";
import DocumentationPro from "@/pages/DocumentationPro";
import CamelionBridge from "@/pages/CamelionBridge";
import Disclaimer from "@/pages/Disclaimer";

function RequireAuth({ children }) {
  const u = auth.user();
  // Belt + suspenders: if either token or user is missing, force a clean redirect
  // to /login. This prevents the "blank dashboard" cold-start case where the user
  // visited a protected URL directly without a session.
  const token = typeof window !== "undefined" ? localStorage.getItem("medlogix_token") : null;
  if (!u || !token) return <Navigate to="/login" replace state={{ from: "auth-required" }} />;
  if (!u.disclaimer_acknowledged_at) return <Navigate to="/disclaimer" replace />;
  if (!u.baa_acknowledged) return <Navigate to="/baa" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/baa" element={<BAA />} />
        <Route path="/reset-2fa" element={<Reset2FA />} />
        <Route path="/2fa-setup" element={<TwoFASetup />} />
        <Route path="/recover" element={<Recovery />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/heart-risk" element={<HeartRisk />} />
        <Route path="/embed/heart-risk" element={<HeartRisk embed />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/security-admin" element={<RequireAuth><SecurityAdmin /></RequireAuth>} />
        <Route path="/admin/leads" element={<RequireAuth><AdminLeads /></RequireAuth>} />
        <Route path="/admin/learning-queue" element={<RequireAuth><LearningQueue /></RequireAuth>} />
        <Route path="/patient-outputs" element={<RequireAuth><PatientOutputsDemo /></RequireAuth>} />
        <Route path="/dictation" element={<RequireAuth><Dictation /></RequireAuth>} />
        <Route path="/encounter-prep" element={<RequireAuth><EncounterPrep /></RequireAuth>} />
        <Route path="/time-saved" element={<RequireAuth><TimeSaved /></RequireAuth>} />
        <Route path="/eod" element={<RequireAuth><EODMode /></RequireAuth>} />
        <Route path="/wellbeing" element={<RequireAuth><Wellbeing /></RequireAuth>} />
        <Route path="/documentation-pro" element={<RequireAuth><DocumentationPro /></RequireAuth>} />
        <Route path="/bridge" element={<RequireAuth><CamelionBridge /></RequireAuth>} />
        <Route path="/charting" element={<RequireAuth><Charting /></RequireAuth>} />
        <Route path="/prior-auth" element={<RequireAuth><PriorAuth /></RequireAuth>} />
        <Route path="/revenue-cycle" element={<RequireAuth><RevenueCycle /></RequireAuth>} />
        <Route path="/liability" element={<RequireAuth><Liability /></RequireAuth>} />
        <Route path="/referrals" element={<RequireAuth><Referrals /></RequireAuth>} />
        <Route path="/staffing" element={<RequireAuth><Staffing /></RequireAuth>} />
        <Route path="/ehr-bridge" element={<RequireAuth><EHRBridge /></RequireAuth>} />
        <Route path="/managed-care" element={<RequireAuth><ManagedCare /></RequireAuth>} />
        <Route path="/patient-engagement" element={<RequireAuth><PatientEngagement /></RequireAuth>} />
        <Route path="/care-coordination" element={<RequireAuth><CareCoordination /></RequireAuth>} />
        <Route path="/hospital-ops" element={<RequireAuth><HospitalOps /></RequireAuth>} />
        <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
        <Route path="/pa-np-hub" element={<RequireAuth><PANPHub /></RequireAuth>} />
        <Route path="/voice-engine" element={<RequireAuth><VoiceEngine /></RequireAuth>} />
        <Route path="/predictive" element={<RequireAuth><Predictive /></RequireAuth>} />
        <Route path="/legal-intelligence" element={<RequireAuth><LegalIntelligence /></RequireAuth>} />
        <Route path="/pharma-manager" element={<RequireAuth><PharmaManager /></RequireAuth>} />
        <Route path="/cme-tracker" element={<RequireAuth><CMETracker /></RequireAuth>} />
        <Route path="/peer-consult" element={<RequireAuth><PeerConsult /></RequireAuth>} />
        <Route path="/financial-wellness" element={<RequireAuth><FinancialWellness /></RequireAuth>} />
        <Route path="/risk-stratification" element={<RequireAuth><RiskStratification /></RequireAuth>} />
        <Route path="/emergency-protocols" element={<RequireAuth><EmergencyProtocols /></RequireAuth>} />
        <Route path="/telehealth" element={<RequireAuth><Telehealth /></RequireAuth>} />
        <Route path="/data-intelligence" element={<RequireAuth><DataIntelligence /></RequireAuth>} />
        <Route path="/reputation-manager" element={<RequireAuth><ReputationManager /></RequireAuth>} />
        <Route path="/contract-intelligence" element={<RequireAuth><ContractIntelligence /></RequireAuth>} />
        <Route path="/outcomes" element={<RequireAuth><Outcomes /></RequireAuth>} />
        <Route path="/cardiology" element={<RequireAuth><Cardiology /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
